/**
 * Social Login Buttons Component
 *
 * Provides Google and GitHub OAuth login buttons.
 * Fetches available providers from the API and renders appropriate buttons.
 */

import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { api, getApiBaseUrl } from '@/lib/api';

interface OAuthProvider {
  id: string;
  name: string;
  enabled: boolean;
}

interface SocialLoginButtonsProps {
  mode?: 'login' | 'register';
  className?: string;
}

const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GitHubIcon = () => (
  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0012 2z"
    />
  </svg>
);

// Default OAuth providers to show when API is unavailable
const DEFAULT_PROVIDERS: OAuthProvider[] = [
  { id: 'google', name: 'Google', enabled: true },
  { id: 'github', name: 'GitHub', enabled: true },
];

export function SocialLoginButtons({
  mode = 'login',
  className,
}: SocialLoginButtonsProps): React.JSX.Element {
  const [providers, setProviders] = React.useState<OAuthProvider[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingProvider, setLoadingProvider] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function fetchProviders() {
      try {
        const data = await api.get<{ providers: OAuthProvider[] }>('/oauth/providers');
        const fetchedProviders = data.providers || [];
        // Use API providers if available, otherwise use defaults
        setProviders(fetchedProviders.length > 0 ? fetchedProviders : DEFAULT_PROVIDERS);
      } catch (error) {
        // Network error - use default providers
        console.error('Failed to fetch OAuth providers, using defaults:', error);
        setProviders(DEFAULT_PROVIDERS);
      } finally {
        setLoading(false);
      }
    }

    fetchProviders();
  }, []);

  const handleOAuthLogin = (providerId: string) => {
    setLoadingProvider(providerId);
    // Redirect to OAuth authorization endpoint using the correct API base URL
    // This ensures it works in both dev (relative path) and production (full URL)
    window.location.href = `${getApiBaseUrl()}/oauth/${providerId}/authorize`;
  };

  const enabledProviders = providers.filter((p) => p.enabled);

  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (enabledProviders.length === 0) {
    return <></>;
  }

  const actionText = mode === 'login' ? 'Sign in' : 'Sign up';

  return (
    <div className={className}>
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with
          </span>
        </div>
      </div>

      <div className="grid gap-3">
        {enabledProviders.map((provider) => {
          const isLoading = loadingProvider === provider.id;
          const Icon = provider.id === 'google' ? GoogleIcon : GitHubIcon;

          return (
            <Button
              key={provider.id}
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => handleOAuthLogin(provider.id)}
              disabled={loadingProvider !== null}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Icon />
              )}
              <span className="ml-2">
                {actionText} with {provider.name}
              </span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export default SocialLoginButtons;
